import { useMemo } from 'react';
import { getText } from 'i18n/intl';
import {
  Divider,
  Form,
  InputNumber,
  Select,
} from '@timeedit/ui-components/antd';
import type { Rule } from '@timeedit/ui-components/antd/form/index';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';
import {
  type BulkEditFieldType,
  BulkEditFormEditValue,
  BulkEditMatchRule,
  type CommonProps,
  getBulkEditFieldOptions,
  getIntegerRangeErrorMessage,
  useBulkEditFieldDisabled,
} from './common';

const BulkEditInteger = ({ field, form }: CommonProps) => {
  const value = Form.useWatch(field.id, form);

  const options = useMemo(
    () =>
      getBulkEditFieldOptions(
        [
          'keep',
          'add',
          'findReplace',
          'replaceAll',
          'removeSpecific',
          'removeAll',
        ],
        field
      ),
    [field.multiple, field.unique, field.mandatory]
  );

  const { disabled, tooltip } = useBulkEditFieldDisabled(field);

  const showAdd = value === 'add' && field.multiple;
  const showFindAndReplace = value === 'findReplace';
  const showReplaceAll = value === 'replaceAll';
  const showRemoveSpecific = value === 'removeSpecific';

  return (
    <LabelWrapper label={field.name} tooltip={tooltip}>
      <div className="bulk-edit-input-wrapper">
        <Form.Item required name={field.id}>
          <Select defaultValue="keep" options={options} disabled={disabled} />
        </Form.Item>
        {showAdd && <Add field={field} />}
        {showFindAndReplace && <FindAndReplace field={field} />}
        {showReplaceAll && <ReplaceAll field={field} />}
        {showRemoveSpecific && <RemoveSpecific field={field} />}
      </div>
    </LabelWrapper>
  );
};

const Add = ({ field }: { field: BulkEditFieldType }) => {
  const rules = useIntegerRules(field);
  return (
    <BulkEditFormEditValue
      name={`${field.id}_add`}
      input={
        <InputNumber
          className="bulk-edit-full-width"
          placeholder={getText(
            'object_manager.bulk-edit-modal.add.input-placeholder'
          )}
        />
      }
      allowMultiple={field.multiple}
      rules={rules}
    />
  );
};

const FindAndReplace = ({ field }: { field: BulkEditFieldType }) => {
  const rules = useIntegerRules(field);
  return (
    <>
      <BulkEditMatchRule fieldId={field.id} isMultiple={field.multiple} />
      <BulkEditFormEditValue
        name={`${field.id}_replace`}
        input={
          <InputNumber
            className="bulk-edit-full-width"
            placeholder={getText(
              'object_manager.bulk-edit-modal.placeholder.search'
            )}
          />
        }
        allowMultiple
        label={getText(
          'object_manager.bulk-edit-modal.findReplace.find-input-label'
        )}
        rules={rules}
      />
      <Divider />
      <BulkEditFormEditValue
        name={`${field.id}_with`}
        input={
          <InputNumber
            className="bulk-edit-full-width"
            placeholder={getText(
              'object_manager.bulk-edit-modal.placeholder.search'
            )}
          />
        }
        label={getText(
          'object_manager.bulk-edit-modal.findReplace.replace-input-label'
        )}
        allowMultiple={field.multiple}
        rules={rules}
      />
    </>
  );
};

const ReplaceAll = ({ field }: { field: BulkEditFieldType }) => {
  const rules = useIntegerRules(field);

  return (
    <BulkEditFormEditValue
      name={`${field.id}_replaceAll`}
      input={
        <InputNumber
          className="bulk-edit-full-width"
          placeholder={getText(
            'object_manager.bulk-edit-modal.replaceAll.input-placeholder'
          )}
        />
      }
      allowMultiple={field.multiple}
      rules={rules}
    />
  );
};

const RemoveSpecific = ({ field }: { field: BulkEditFieldType }) => {
  const rules = useIntegerRules(field);

  return (
    <BulkEditFormEditValue
      name={`${field.id}_removeSpecific`}
      input={
        <InputNumber
          className="bulk-edit-full-width"
          placeholder={getText(
            'object_manager.bulk-edit-modal.removeSpecific.input-placeholder'
          )}
        />
      }
      allowMultiple
      rules={rules}
    />
  );
};

const useIntegerRules = (field: BulkEditFieldType): Rule[] => {
  return useMemo(
    () => [
      {
        type: 'integer',
        min: field.min,
        max: field.max,
        required: true,
        message: getIntegerRangeErrorMessage(field.min, field.max),
      },
    ],
    [field]
  );
};

export default BulkEditInteger;
